import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable
} from "aurelia-framework";
import nameof from "../../../../common/nameof";
import { FormConditionValidationEnum } from "../../../../enums/form-condition-validation-enum";
import { FormValidationEnum } from "../../../../enums/form-validation-enum";
import { IAncestorSettings, IInputOptions } from "../../../../interfaces/form-builder/i-input-options";
import { IMultiSelectOptions } from "../../../../interfaces/form-builder/i-multi-select-options";
import { IConditionalValidation, IRequiredAllValidation } from "../../../../interfaces/form-builder/i-validation";
import { NoteDataManager } from "../../note-data-manager";
import { cleanElementCode, setElementPillColor } from "../../../../common/utilities/note-pill-formatting";
import { IOption, IElement } from "../../../../interfaces/form-builder/i-element";
import { IScrubberResults } from "../../../../interfaces/form-builder/i-scrubber";
import { InputTypesEnum } from "../input-types-enum";
import { IInput } from "../../../../interfaces/form-builder/i-input";

@customElement("multi-select")
@autoinject
export class MultiSelect {
    // Input fields
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isAi: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public result: string[] = [];
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public viewOptions: IMultiSelectOptions;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    private _resultCache: string[] = [];
    private _bindingEngine: BindingEngine;
    private _resultSubscription: Disposable;
    public selectedOptions: string[] = []; // This middle man list exists so we can track user changes before any subInput is attached
    public noteDataManager: NoteDataManager;
    public masterOptionValue: string = null;
    public showOption: boolean[] = [];
    public showRequiredIcon: boolean = false;
    public userToggledOptions: Set<string> = new Set<string>();
    public scrubberResults: IScrubberResults;

    @computedFrom(nameof<MultiSelect>("viewOptions"))
    public get hasSubInputs(): boolean {
        return this.viewOptions.options.filter((e) => e.subElement).length > 0;
    }

    @computedFrom(`${nameof<MultiSelect>("viewOptions")}.${nameof<IInputOptions>("options")}`, "selectedOptions.length")
    public get showSubInput() {
        return this.viewOptions.options.map((opt) => this.selectedOptions.indexOf(opt.value) > -1);
    }

    public constructor(bindingEngine: BindingEngine, noteDataManager: NoteDataManager) {
        this._bindingEngine = bindingEngine;
        this.noteDataManager = noteDataManager;
    }

    public attached() {
        // https://ilikekillnerds.com/2015/10/observing-objects-and-arrays-in-aurelia/
        // https://dustindavis.me/observing-arrays-aurelia/
        this.scrubberResults = this.noteDataManager.getScrubberResults();
        this.showOption = new Array(this.viewOptions.options.length).fill(true);
        let masterOptionBehavior = null;
        if (this.viewOptions.behaviors) {
            masterOptionBehavior = this.viewOptions.behaviors.filter(
                (behave) => behave.behaviorType === "masteroption"
            );
            if (masterOptionBehavior.length > 0) {
                let masterOptionName = masterOptionBehavior[0].sourceName;
                let masterOption = this.viewOptions.options.filter((option) => option.name === masterOptionName);
                if (masterOption.length > 0) {
                    this.masterOptionValue = masterOption[0].value;
                }
            }
        }
        this.noteDataManager.assignValidationRules.call(this, this.viewOptions.name, this.noteDataManager);
        this.initRequiredIndicator();
        this.initResultSubscription();
    }

    // NOTE: This would be called by the aurelia input when the result array changes reference
    public resultChanged(newValue: string) {
        this.initResultSubscription();
        this.updateResults(true);
    }

    public initResultSubscription() {
        this._resultSubscription = this._bindingEngine.collectionObserver(this.result).subscribe((data) => {
            this.updateResults(false);
        });
    }

    public updateResults(initialLoad: boolean) {
        if (this.masterOptionValue) {
            this.showOption = this.viewOptions.options.map((option) => {
                if (option.value == this.masterOptionValue) {
                    return true;
                } else {
                    return !this.result.includes(this.masterOptionValue);
                }
            });
            if (this.result.includes(this.masterOptionValue) && this.result.length !== 1) {
                this._resultCache = this.result.filter((result) => result != this.masterOptionValue);
                this.result.splice(0, this.result.length);
                this.result.push(this.masterOptionValue);
            }
            if (this.result.length == 0 && this._resultCache.length != 0) {
                this.result.push(...this._resultCache);
                this._resultCache = [];
            }
        }

        if (!initialLoad) {
            this.result.forEach((option) => this.userToggledOptions?.add(option));

            // When an option is deselected, find all sub-element fields under it and explicitly clear them in _modifiedData.
            const deselectedValues = this.selectedOptions.filter((value) => !this.result.includes(value));
            if (deselectedValues.length > 0) {
                this._resultSubscription?.dispose();
                deselectedValues.forEach((deselectedValue) => {
                    const deselectedOption = this.viewOptions.options.find((opt) => opt.value === deselectedValue);
                    if (deselectedOption?.subElement) {
                        this.clearSubElementFields(deselectedOption.subElement);
                    }
                });
                this.initResultSubscription();
            }
        }

        this.selectedOptions.splice(0, this.selectedOptions.length, ...this.result);
    }

    private clearSubElementFields(element: IElement) {
        if (!element) {
            return;
        }
        if (element.elements?.length > 0) {
            element.elements.forEach((child: IElement | IInput) => this.clearSubElementFields(child));
            return;
        }
        if ((element as IOption).subElement) {
            this.clearSubElementFields((element as IOption).subElement);
        }
        if (element.name) {
            // Preserve the the initial prop value of noteModel if inputType is "hidden" and belongs to the "limit-options" valueDependencies.
            // It controls the visibility of nested hidden options.
            const isHiddenHiderElement =
                (element as IInput).inputType === InputTypesEnum.Hidden && element.name.endsWith("Hider");

            if (!isHiddenHiderElement) {
                Promise.resolve().then(() => this.noteDataManager.clearPropInModifiedData(element.name));
            }
        }
    }

    public getCheckBoxId(viewOptionName: string, optionName: string) {
        return `${viewOptionName}-${optionName}`;
    }

    private initRequiredIndicator() {
        let validations = this.noteDataManager.getValidationFromProp(this.viewOptions?.name);
        validations?.forEach((rule: any) => {
            if (rule.validationType === FormValidationEnum.Conditional) {
                let conditionalValidation = rule as IConditionalValidation;
                let conditionMethod: () => boolean = null;
                if (conditionalValidation.conditionType === FormConditionValidationEnum.IsNotEmpty) {
                    conditionMethod = () => !!this.noteDataManager.getValue(conditionalValidation.targetName);
                } else if (conditionalValidation.conditionType === FormConditionValidationEnum.IsEmpty) {
                    conditionMethod = () => !this.noteDataManager.getValue(conditionalValidation.targetName);
                }
                if (!!conditionMethod) {
                    this.checkIfRequired(conditionalValidation.conditionValidation, conditionMethod);
                }
            } else {
                this.checkIfRequired(rule);
            }
        });
    }

    public checkIfRequired(rule: any, condition: () => boolean = () => true) {
        if (rule.validationType === FormValidationEnum.RequiredAll) {
            let requiredAllRule = rule as IRequiredAllValidation;
            this.showRequiredIcon = requiredAllRule.showIcon && condition();
        }
    }

    public hasErrors(element: IOption) {
        return this.scrubberResults.errors.some((item) => item.questions.indexOf(cleanElementCode(element)) > -1);
    }

    public hasWarning(element: IOption) {
        return this.scrubberResults.warnings.some((item) => item.questions.indexOf(cleanElementCode(element)) > -1);
    }

    public hasInconsistency(element: IOption) {
        return this.scrubberResults.inconsistencies.some(
            (item) => item.questions.indexOf(cleanElementCode(element)) > -1
        );
    }

    public checkColor(option: IOption) {
        return setElementPillColor(option);
    }

    public checkCode(option: IOption) {
        return cleanElementCode(option);
    }

    public optionAncestorSettings(value: string) {
        let optionSettings = Object.assign({}, this.ancestorSettings);
        optionSettings.isUserToggled = this.userToggledOptions.has(value);
        return optionSettings;
    }

    public isOptionInputAi(value: string) {
        if (this.isAi) {
            let AiValues = this.noteDataManager.getAiValueFromName(this.viewOptions.name) as string[];
            return AiValues?.length > 0 && AiValues.includes(value);
        }
        return false;
    }

    public detached() {
        this._resultSubscription?.dispose();
    }
}
